import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bot, Menu, X, ChevronDown, Zap, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { ThemeToggle } from '../ThemeToggle';

const LanguageDropdown: React.FC<{
  language: string;
  onToggle: () => void;
}> = ({ language, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-all duration-300 p-2 rounded-lg hover:bg-[var(--brand-primary)]/10 group"
      >
        <span className="text-lg group-hover:scale-110 transition-transform duration-300">
          {language === 'en' ? '🇬🇧' : '🇩🇪'}
        </span>
        <ChevronDown size={16} className={`transition-all duration-300 ${isOpen ? 'rotate-180 text-[var(--brand-primary)]' : 'group-hover:text-[var(--brand-primary)]'}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-[var(--bg-secondary)] rounded-xl shadow-xl border border-[var(--border-primary)]/50 overflow-hidden z-[1000] backdrop-blur-sm animate-in slide-in-from-top-2 duration-200">
          <div className="py-2">
            <button
              onClick={() => {
                if (language !== 'en') {
                  onToggle();
                }
                setIsOpen(false);
              }}
              className={`flex items-center space-x-3 w-full px-4 py-3 text-sm transition-all duration-200 ${
                language === 'en' 
                  ? 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]' 
                  : 'text-[var(--text-secondary)] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5'
              }`}
            >
              <span className="text-lg">🇬🇧</span>
              <span className="font-medium">English</span>
            </button>
            <button
              onClick={() => {
                if (language !== 'de') {
                  onToggle();
                }
                setIsOpen(false);
              }}
              className={`flex items-center space-x-3 w-full px-4 py-3 text-sm transition-all duration-200 ${
                language === 'de' 
                  ? 'bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]' 
                  : 'text-[var(--text-secondary)] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/5'
              }`}
            >
              <span className="text-lg">🇩🇪</span>
              <span className="font-medium">Deutsch</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const FeaturesDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { language } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const features = [
    {
      to: `/${language}/pdf-to-xrechnung`,
      title: t('nav.pdfToXRechnung'),
      icon: <Zap className="w-4 h-4" />
    },
    {
      to: `/${language}/pdf-to-cii`,
      title: t('convert.pdfToCii', { defaultValue: 'PDF to XRechnung (CII)' }),
      icon: <Zap className="w-4 h-4" />,
      description: t('header.features.pdfToCii.description', { defaultValue: 'When your accounting system prefers CII format' }),
    },
    {
      to: `/${language}/pdf-to-zugferd`,
      title: t('nav.pdfToZugferd'),
      icon: <Bot className="w-4 h-4" />
    },
    {
      to: `/${language}/xrechnung-to-pdf`,
      title: t('nav.xrechnungToPdf'),
      icon: <Sparkles className="w-4 h-4" />
    },
    {
      to: `/${language}/xrechnung-viewer`,
      title: t('nav.xrechnungViewer'),
      icon: <Sparkles className="w-4 h-4" />
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-all duration-300 px-3 py-2 rounded-lg hover:bg-[var(--brand-primary)]/10 group"
      >
        <span className="font-medium">{t('nav.features')}</span>
        <ChevronDown size={16} className={`transition-all duration-300 ${isOpen ? 'rotate-180 text-[var(--brand-primary)]' : 'group-hover:text-[var(--brand-primary)]'}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-[var(--bg-secondary)] rounded-xl shadow-xl border border-[var(--border-primary)]/50 overflow-hidden z-[1000] backdrop-blur-sm animate-in slide-in-from-top-2 duration-200">
          <div className="py-2">
            {features.map((feature, index) => (
              <Link
                key={feature.to}
                to={feature.to}
                className="group flex items-center space-x-3 px-4 py-3 text-sm hover:bg-[var(--brand-primary)]/5 transition-all duration-200"
                onClick={() => setIsOpen(false)}
              >
                <div className="p-2 bg-[var(--brand-primary)]/10 rounded-lg group-hover:bg-[var(--brand-primary)]/20 transition-colors duration-200">
                  <div className="text-[var(--brand-primary)] group-hover:scale-110 transition-transform duration-200">
                    {feature.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-[var(--text-primary)] group-hover:text-[var(--brand-primary)] transition-colors duration-200">
                    {feature.title}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'de' : 'en';
    setLanguage(newLang);
    
    // Update URL to reflect new language
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(/^\/(en|de)/, `/${newLang}`);
    navigate(newPath);
  };

  const handlePricingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-md border-b border-[var(--border-primary)]/50">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand-primary)]/5 via-transparent to-[var(--brand-primary)]/5 opacity-50"></div>
      
      <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Enhanced Logo */}
        <Link 
          to="/" 
          className="group flex items-center space-x-3 text-[var(--brand-primary)] font-bold text-xl hover:opacity-90 transition-all duration-300"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-[var(--brand-primary)]/20 rounded-lg blur-sm group-hover:blur-md transition-all duration-300"></div>
            <Bot className="relative h-8 w-8 text-[var(--brand-primary)] group-hover:scale-110 transition-transform duration-300" />
          </div>
          <div className="flex items-center">
            <span className="text-sm sm:text-base md:text-xl text-[var(--brand-primary)] font-bold whitespace-nowrap">
              <span className="hidden sm:inline">Invoice-Converter.com</span>
              <span className="sm:hidden">Invoice-Converter</span>
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-2">
          <FeaturesDropdown />
          
          <Link
            to={`/${language}/blog`}
            className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-[var(--brand-primary)]/10 relative group"
          >
            <span className="relative z-10">{t('nav.blog')}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand-primary)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
          </Link>
          
          <a
            href="#pricing"
            onClick={handlePricingClick}
            className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-[var(--brand-primary)]/10 relative group"
          >
            <span className="relative z-10">{t('nav.pricing')}</span>
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand-primary)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
          </a>

          {/* Auth Links */}
          <div className="flex items-center space-x-3 ml-4">
            {user ? (
              <Link
                to={`/${language}/profile`}
                className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 border border-[var(--border-secondary)] hover:border-[var(--brand-primary)]/50 hover:bg-[var(--brand-primary)]/5"
              >
                {t('nav.profile')}
              </Link>
            ) : (
              <Link
                to={`/${language}/sign-in`}
                className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 border border-[var(--border-secondary)] hover:border-[var(--brand-primary)]/50 hover:bg-[var(--brand-primary)]/5"
              >
                {t('nav.signin')}
              </Link>
            )}
            
            {/* Enhanced CTA Button */}
            <Link
              to={`/${language}/convert`}
              className="group relative bg-[var(--brand-primary)] text-[var(--text-button)] inline-flex items-center px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 hover:opacity-90 hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-primary)]/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Zap className="relative w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 group-hover:animate-pulse" />
              <span className="relative">
                <span className="hidden md:inline">{t('nav.convert')}</span>
                <span className="md:hidden">{t('nav.convertShort') || 'Convert'}</span>
              </span>
            </Link>
          </div>

          {/* Theme & Language Controls */}
          <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-[var(--border-primary)]/30">
            <ThemeToggle />
            <LanguageDropdown language={language} onToggle={toggleLanguage} />
          </div>
        </div>

        {/* Enhanced Mobile Navigation */}
        <div className="md:hidden flex items-center gap-3">
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <LanguageDropdown language={language} onToggle={toggleLanguage} />
          </div>
          
          {/* Mobile CTA */}
          <Link
            to={`/${language}/convert`}
            className="bg-[var(--brand-primary)] text-[var(--text-button)] px-3 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-all duration-300 shadow-lg flex items-center gap-1"
          >
            <Zap className="w-3 h-3" />
            <span className="hidden xs:inline">{t('nav.convertMobile')}</span>
            <span className="xs:hidden">{t('header.mobile.convert', { defaultValue: 'Convert' })}</span>
          </Link>
          
          {/* Enhanced Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="relative p-2 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-all duration-300 rounded-lg hover:bg-[var(--brand-primary)]/10"
          >
            <div className="relative w-6 h-6">
              <Menu 
                size={24} 
                className={`absolute inset-0 transition-all duration-300 ${isMenuOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'}`} 
              />
              <X 
                size={24} 
                className={`absolute inset-0 transition-all duration-300 ${isMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'}`} 
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Enhanced Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="absolute top-16 left-0 w-full bg-[var(--bg-primary)] backdrop-blur-md border-t border-[var(--border-primary)] shadow-lg md:hidden animate-in slide-in-from-top-2 duration-300">
          <div className="px-4 py-6 space-y-4">
            {/* Mobile Features Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--brand-primary)]" />
                {t('nav.features')}
              </h3>
              <div className="pl-6 space-y-2">
                <Link
                  to={`/${language}/pdf-to-xrechnung`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Zap className="w-4 h-4" />
                  <span className="text-sm">{t('nav.pdfToXRechnung')}</span>
                </Link>
                <Link
                  to={`/${language}/pdf-to-cii`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Zap className="w-4 h-4" />
                  <span className="text-sm">{t('convert.pdfToCii', { defaultValue: 'PDF to XRechnung (CII)' })}</span>
                </Link>
                <Link
                  to={`/${language}/ubl-to-cii`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Bot className="w-4 h-4" />
                  <span className="text-sm">{t('convert.ublToCii', { defaultValue: 'XRechnung UBL to CII' })}</span>
                </Link>
                <Link
                  to={`/${language}/pdf-to-zugferd`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Bot className="w-4 h-4" />
                  <span className="text-sm">{t('nav.pdfToZugferd')}</span>
                </Link>
                <Link
                  to={`/${language}/xrechnung-to-pdf`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">{t('nav.xrechnungToPdf')}</span>
                </Link>
                <Link
                  to={`/${language}/xrechnung-viewer`}
                  className="flex items-center gap-3 text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-2 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">{t('nav.xrechnungViewer')}</span>
                </Link>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-[var(--border-primary)]/30"></div>

            {/* Other Links */}
            <div className="space-y-2">
              <Link
                to={`/${language}/blog`}
                className="block text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-3 px-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--brand-primary)]/5"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.blog')}
              </Link>
              <a
                href="#pricing"
                onClick={handlePricingClick}
                className="block text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-3 px-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--brand-primary)]/5"
              >
                {t('nav.pricing')}
              </a>
              {user ? (
                <Link
                  to={`/${language}/profile`}
                  className="block text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-3 px-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--brand-primary)]/5 border border-[var(--border-secondary)] hover:border-[var(--brand-primary)]/30"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.profile')}
                </Link>
              ) : (
                <Link
                  to={`/${language}/sign-in`}
                  className="block text-[var(--text-secondary)] hover:text-[var(--brand-primary)] py-3 px-3 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-[var(--brand-primary)]/5 border border-[var(--border-secondary)] hover:border-[var(--brand-primary)]/30"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('nav.signin')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;